import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface PageModalProps {
  open: boolean;
  title?: string;
  theme?: string;
  modalId: string;
  children?: ReactNode;
  onClose: () => void;
}

export function PageModal({
  open,
  title = '',
  theme: themeProp,
  children,
  onClose,
}: PageModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const dialogRef = useRef<HTMLDivElement>(null);
  const dragStartMouseRef = useRef({ x: 0, y: 0 });
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const positionRef = useRef({ x: 0, y: 0 });

  // Keep refs in sync
  isDraggingRef.current = isDragging;
  positionRef.current = position;

  // Handle open/close
  useEffect(() => {
    if (open) {
      document.body.classList.add('kstc-scroll-lock');
      setPosition({ x: 0, y: 0 });
    } else {
      document.body.classList.remove('kstc-scroll-lock');
    }

    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [open]);

  // Escape key handler
  useEffect(() => {
    if (!open) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Mouse move handler for dragging
  const onMouseMove = useCallback((event: MouseEvent) => {
    if (!isDraggingRef.current) return;

    const deltaX = event.clientX - dragStartMouseRef.current.x;
    const deltaY = event.clientY - dragStartMouseRef.current.y;

    let newX = dragStartPosRef.current.x + deltaX;
    let newY = dragStartPosRef.current.y + deltaY;

    // Constrain to viewport
    const dialog = dialogRef.current;
    if (dialog && dialog.parentElement) {
      const parentRect = dialog.parentElement.getBoundingClientRect();
      const dialogRect = dialog.getBoundingClientRect();

      const minVisible = 100;
      const maxX = (parentRect.width - minVisible) / 2;
      const minX = -(parentRect.width - minVisible) / 2;
      const maxY = (parentRect.height - minVisible) / 2;
      const minY = -(parentRect.height - dialogRect.height) / 2;

      newX = Math.max(minX, Math.min(maxX, newX));
      newY = Math.max(minY, Math.min(maxY, newY));
    }

    setPosition({ x: newX, y: newY });
  }, []);

  // Mouse up handler for dragging
  const onMouseUp = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  // Header mouse down for dragging
  const onHeaderMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (event.button !== 0) return;
      if ((event.target as HTMLElement).closest('.kstc-page-close')) return;

      setIsDragging(true);
      isDraggingRef.current = true;
      dragStartMouseRef.current = { x: event.clientX, y: event.clientY };
      dragStartPosRef.current = { x: positionRef.current.x, y: positionRef.current.y };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      event.preventDefault();
    },
    [onMouseMove, onMouseUp]
  );

  // Cleanup drag listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [onMouseMove, onMouseUp]);

  // Backdrop click
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const dialogStyle: React.CSSProperties =
    position.x === 0 && position.y === 0
      ? {}
      : { transform: `translate(${position.x}px, ${position.y}px)` };

  if (!open) return null;

  return (
    <div
      data-kstc-modal
      className={`kstc-backdrop ${theme}`}
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        className={`kstc-dialog kstc-dialog--page${isDragging ? ' kstc-dialog--dragging' : ''}`}
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
      >
        <div className="kstc-page-header" onMouseDown={onHeaderMouseDown}>
          <div className="kstc-page-title">
            <i className="fa fa-window-maximize kstc-page-icon" />
            <span>{title}</span>
          </div>
          <button
            className="kstc-page-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="kstc-page-body">
          <div className="kstc-page-content">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
